/**
 * Video detection script that runs in the WebView context to monitor video playback state.
 * Detects video activity through:
 * - Direct video element event listeners (play/pause/ended)
 * - MutationObserver for dynamic content changes and new video elements
 * - Cleanup on page unload and re-initialization for dynamic sites
 * Reports state changes to React Native via postMessage with debug logging.
 */

export function getVideoDetectionScript(): string {
  return `
    (function() {
      let isAnyVideoPlaying = false;
      let lastReportedState = null;
      let mutationObserver = null;
      let trackedVideos = new Set();
      let isInitialized = false;
      let mutationDebounceTimer = null;

      function debugLog(message, ...args) {
        const logMessage = "[ReactNative-VideoDetection] " + message;
        console.log(logMessage, ...args);
        
        // Send debug message to React Native
        if (window.ReactNativeWebView) {
          try {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: "debug",
                message: logMessage,
                args: args.length > 0 ? args : undefined,
                timestamp: Date.now(),
              })
            );
          } catch (e) {
            console.log("Failed to send debug message to RN:", e);
          }
        }
      }

      debugLog("Starting enhanced video detection for React Native...");

      function reportVideoActivity(isPlaying) {
        if (lastReportedState !== isPlaying) {
          lastReportedState = isPlaying;
          debugLog("🎬 VIDEO STATE CHANGED:", isPlaying ? "▶️ PLAYING" : "⏸️ PAUSED");
          
          if (window.ReactNativeWebView) {
            try {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "videoActivity",
                  isPlaying: isPlaying,
                  timestamp: Date.now(),
                })
              );
            } catch (e) {
              debugLog("❌ Failed to send video state to React Native:", e);
            }
          } else {
            debugLog("❌ ReactNativeWebView not available");
          }
        }
      }

      function updatePlaybackState() {
        // Check if any tracked video is currently playing
        const anyPlaying = Array.from(trackedVideos).some(video => 
          !video.paused && !video.ended
        );
        
        // Debug video states
        Array.from(trackedVideos).forEach((video, index) => {
          debugLog(\`🎥 Video \${index}: paused=\${video.paused}, ended=\${video.ended}, readyState=\${video.readyState}, currentTime=\${video.currentTime.toFixed(2)}\`);
        });
        
        
        if (anyPlaying !== isAnyVideoPlaying) {
          isAnyVideoPlaying = anyPlaying;
          reportVideoActivity(anyPlaying);
        }
      }

      function addVideoListeners(video) {
        // Skip if we're already tracking this video
        if (trackedVideos.has(video)) {
          return;
        }

        debugLog("📹 Adding listeners to new video element");
        trackedVideos.add(video);

        const playHandler = function() {
          debugLog("🎬 Video PLAY event");
          // Small delay to allow video properties to update
          setTimeout(function() {
            updatePlaybackState();
          }, 50);
        };

        const pauseHandler = function() {
          debugLog("⏸️ Video PAUSE event");
          updatePlaybackState();
        };

        const endedHandler = function() {
          debugLog("🏁 Video ENDED event");
          updatePlaybackState();
        };

        // Store handlers on the video element for cleanup
        video._rnVideoHandlers = {
          play: playHandler,
          pause: pauseHandler,
          ended: endedHandler
        };

        video.addEventListener("play", playHandler);
        video.addEventListener("pause", pauseHandler);
        video.addEventListener("ended", endedHandler);
      }

      function removeVideoListeners(video) {
        if (!trackedVideos.has(video) || !video._rnVideoHandlers) {
          return;
        }

        debugLog("🧹 Removing listeners from video element");
        
        video.removeEventListener("play", video._rnVideoHandlers.play);
        video.removeEventListener("pause", video._rnVideoHandlers.pause);
        video.removeEventListener("ended", video._rnVideoHandlers.ended);
        
        delete video._rnVideoHandlers;
        trackedVideos.delete(video);
      }

      function scanForVideos(container = document) {
        const videos = container.querySelectorAll("video");
        debugLog(\`🔍 Found \${videos.length} video elements in scan\`);

        videos.forEach(addVideoListeners);
        
        // Clean up any tracked videos that are no longer in the DOM
        const currentVideos = new Set(document.querySelectorAll("video"));
        trackedVideos.forEach(trackedVideo => {
          if (!currentVideos.has(trackedVideo)) {
            removeVideoListeners(trackedVideo);
          }
        });

        // Update state after scanning
        updatePlaybackState();
      }

      function handleMutations(mutations) {
        let hasNewVideos = false;

        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the added node is a video or contains videos
                if (node.tagName === 'VIDEO') {
                  hasNewVideos = true;
                } else if (node.querySelectorAll && node.querySelectorAll('video').length > 0) {
                  hasNewVideos = true;
                }
              }
            });
          }
        });

        if (hasNewVideos) {
          // Debounce to avoid excessive calls during rapid DOM changes
          if (mutationDebounceTimer) {
            clearTimeout(mutationDebounceTimer);
          }
          
          mutationDebounceTimer = setTimeout(function() {
            debugLog("🆕 New video elements detected, scanning...");
            scanForVideos();
            mutationDebounceTimer = null;
          }, 500);
        }
      }

      function initialize() {
        if (isInitialized) {
          debugLog("⚠️ Already initialized, skipping");
          return;
        }

        debugLog("🚀 Initializing video detection");
        
        // Initial scan for existing videos
        scanForVideos();

        // Set up MutationObserver for dynamic content
        mutationObserver = new MutationObserver(handleMutations);
        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true
          // Removed attribute watching as we only care about new video elements
        });

        isInitialized = true;
        debugLog("✅ Video detection initialized successfully");
      }

      function cleanup() {
        debugLog("🧹 Cleaning up video detection");
        
        // Remove all video listeners
        trackedVideos.forEach(removeVideoListeners);
        trackedVideos.clear();

        // Disconnect mutation observer
        if (mutationObserver) {
          mutationObserver.disconnect();
          mutationObserver = null;
        }

        // Clear any pending timers
        if (mutationDebounceTimer) {
          clearTimeout(mutationDebounceTimer);
          mutationDebounceTimer = null;
        }

        isInitialized = false;
      }

      // Initialize when DOM is ready
      if (document.readyState === "loading") {
        debugLog("⏳ DOM loading, waiting for DOMContentLoaded");
        document.addEventListener("DOMContentLoaded", initialize);
      } else {
        debugLog("✅ DOM already ready, initializing immediately");
        initialize();
      }

      // Fallback initialization for edge cases
      setTimeout(function() {
        if (!isInitialized) {
          debugLog("⚠️ Fallback initialization after 2 seconds");
          initialize();
        }
      }, 2000);

      // Clean up when page unloads
      window.addEventListener("beforeunload", cleanup);
      
      // Handle page visibility changes (optional optimization)
      document.addEventListener("visibilitychange", function() {
        if (document.visibilityState === 'visible' && isInitialized) {
          // Re-check state when page becomes visible
          updatePlaybackState();
        }
      });
    })(); 
    true;
  `
}
